import { COMMUNITY_TAB, DIRECTORY_TYPE, MESSAGE_STATUS } from '@/types/enums';
import type { GetCalendarRouteParams } from '@/types';

export enum ROUTE_ALIASES {
  AUTH_CALLBACK_PAGE = '/callback',
  LOGIN = '/login',
  LOGOUT = '/logout',
  ERROR = '/error',
  HOME = '/',
  GROUPS = '/groups',
  GROUPS_ID = ':id',
  GROUPS_SETTINGS = 'settings',
  CALENDAR = '/calendar',

  PARISH_INFO_ROOT = '/parish-info',
  PARISH_DETAILS_ROOT = '/parish-details',
  PARISH_DETAILS_ID = ':id',
  PARISH_DETAILS = ':id/:tab',
  PARISH_DETAILS_INFO_TYPE = ':id/info/:directoryType?',
  PARISH_DETAILS_DIRECTORY = ':id/directory/:directoryType?',
  PARISH_EDIT = 'edit',

  APP_SETUP_ROOT = '/app-setup',
  APP_SETUP = ':tab/:enabledState?',

  MEMBERS = '/members',

  COMMUNITY = '/community',
  COMMUNITY_KIND = ':kind',
  COMMUNITY_LISTS = 'lists',
  COMMUNITY_LIST_ID = ':listId',
  COMMUNITY_LIST_SETTINGS = 'settings',

  MESSAGES = '/messages',
  MESSAGES_CREATE = '/messages/create',
  MESSAGES_INBOX = '/messages/inbox',
  MESSAGES_SENT = '/messages/sent',
  MESSAGES_ANNOUNCEMENTS = '/messages/announcements',
  MESSAGES_TEMPLATES = '/messages/templates',
  MESSAGES_SCHEDULED = '/messages/scheduled',
  MESSAGES_TEXT_STATUS = '/messages/text-status',
  MESSAGES_DRAFTS = '/messages/drafts',
  MESSAGES_DELETED = '/messages/deleted',

  FORMS = '/forms',
  FORMS_CREATE = '/forms/create',
  FORMS_PUBLISHED = '/forms/published',
  FORMS_UNPUBLISHED = '/forms/unpublished',
  FORMS_TEMPLATES = '/forms/templates',
  FORMS_DRAFTS = '/forms/drafts',
  FORMS_BUILDER_ROOT = '/forms/builder',
  FORMS_BUILDER_ID = '/forms/builder/:id',
  FORMS_PUBLISHED_SUCCESS_ROOT = '/forms/published-success',
  FORMS_PUBLISHED_SUCCESS_ID = '/forms/published-success/:id',
  FORMS_RESULTS_ROOT = '/forms/results',
  FORMS_RESULTS_ID = '/forms/results/:id',

  MINISTRIES = '/ministries',
  MINISTRIES_CALENDAR_VIEW = '/ministries/calendar-view',
  MINISTRIES_SCHEDULING = '/ministries/scheduling',
  MINISTRIES_GROUP = '/ministries/group',
  MINISTRIES_GROUP_ID = ':id',
  MINISTRIES_VOLUNTEERS = '/ministries/volunteers',
  MINISTRIES_VOLUNTEER_ID = '/ministries/volunteers/:id',
  MINISTRIES_REPORTS = '/ministries/reports',
  MINISTRIES_SETTINGS = '/ministries/settings',

  PROFILE = '/profile',

  MESSAGE = '/message',
  MESSAGE_DESIGNER = '/message/designer',

  PLAN_AHEAD = '/plan-ahead',
  SHOP = '/shop',
  WEBSITE = '/website',
  BULLETIN = '/bulletin',
  ART_LIBRARY = '/art-library',
  MIO = '/mio',
  NOT_FOUND = '*',
}

export const enum APP_SETUP_TAB_NAME {
  DEFAULT = 'default',
  CUSTOM = 'custom',
  BACKGROUND_IMAGE = 'background-image',
  PROMOTION_ASSETS = 'promotion-assets',
  PARISH_ADMIN = 'parish-admin',
  GROUP_ADMIN = 'group-admin',
}

export const enum APP_SETUP_ENABLED_STATE {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export const enum PARISH_DETAILS_TAB_NAME {
  INFO = 'info',
  DIRECTORY = 'directory',
  MASS_TIME = 'mass-time',
  CUSTOMIZATION_FIELDS = 'customization-fields',
  RELATED_PARISHES = 'related-parishes',
}

export const enum VOLUNTEERS_TAB {
  VOLUNTEERS = 'volunteers',
  FAMILIES = 'families',
  TEAMS = 'teams',
  ROTATIONAL_SCHEDULE = 'rotational-schedule',
}

export const enum VOLUNTEER_DETAILS_TAB {
  GENERAL = 'general',
  AVAILABILITY = 'availability',
  UNAVAILABLE = 'unavailable',
  ATTENDANCE_REPORT = 'attendance-report',
}

export const enum PARISH_DETAILS_CONTEXT_MODE {
  CLUSTER_ONE = 'cluster-one',
}

export const enum PARISH_DETAILS_QUERY_PARAM {
  CONTEXT_MODE = 'contextMode',
  CLUSTERED_CHURCH_ID = 'clusteredChurchId',
  CLUSTERED_CHURCH_NAME = 'clusteredChurchName',
}

type GetDetailsRouteWithQueryParams = {
  id: string;
  tab?: string;
  directoryType?: DIRECTORY_TYPE;
  query?: Partial<Record<PARISH_DETAILS_QUERY_PARAM, string>>;
};

export const Links = {
  parishInfo: {
    getDetailsRoute: (id: string, tab?: string, directoryType?: DIRECTORY_TYPE) => {
      if (tab === PARISH_DETAILS_TAB_NAME.DIRECTORY) {
        return `${ROUTE_ALIASES.PARISH_DETAILS_ROOT}/${id}/${PARISH_DETAILS_TAB_NAME.DIRECTORY}${directoryType ? `/${directoryType}` : `/${DIRECTORY_TYPE.PARISH}`}`;
      }
      if (tab === PARISH_DETAILS_TAB_NAME.INFO) {
        return `${ROUTE_ALIASES.PARISH_DETAILS_ROOT}/${id}/${PARISH_DETAILS_TAB_NAME.INFO}${directoryType ? `/${directoryType}` : `/${DIRECTORY_TYPE.PARISH}`}`;
      }
      return `${ROUTE_ALIASES.PARISH_DETAILS_ROOT}/${id}${tab ? `/${tab}` : ''}`;
    },
    getDetailsRouteWithQuery: ({ id, tab, directoryType, query }: GetDetailsRouteWithQueryParams) => {
      const baseRoute = Links.parishInfo.getDetailsRoute(id, tab, directoryType);

      if (!query) {
        return baseRoute;
      }

      const queryEntries = Object.entries(query).filter((entry): entry is [string, string] => Boolean(entry[1]));
      const queryParams = new URLSearchParams(queryEntries);
      const queryString = queryParams.toString();
      return queryString ? `${baseRoute}?${queryString}` : baseRoute;
    },
    getMassTimesCalendarRoute: (id: string) =>
      `${ROUTE_ALIASES.PARISH_DETAILS_ROOT}/${id}/${PARISH_DETAILS_TAB_NAME.MASS_TIME}?showToday=true`,
  },
  appSetup: {
    getModulesRoute: (tab: APP_SETUP_TAB_NAME, enabledState: APP_SETUP_ENABLED_STATE) => {
      if (tab !== APP_SETUP_TAB_NAME.CUSTOM) {
        return `${ROUTE_ALIASES.APP_SETUP_ROOT}/${tab}`;
      }
      return `${ROUTE_ALIASES.APP_SETUP_ROOT}/${tab}/${enabledState}`;
    },
    getBackgroundImageRoute: () => `${ROUTE_ALIASES.APP_SETUP_ROOT}/${APP_SETUP_TAB_NAME.BACKGROUND_IMAGE}`,
  },
  getMembersRoute: () => `${ROUTE_ALIASES.MEMBERS}`,
  getMinistryGroupRoute: (groupId?: string) =>
    groupId ? `${ROUTE_ALIASES.MINISTRIES_GROUP}/${groupId}` : ROUTE_ALIASES.MINISTRIES_GROUP,
  getMinistryVolunteersRoute: (tab: VOLUNTEERS_TAB = VOLUNTEERS_TAB.VOLUNTEERS) =>
    `${ROUTE_ALIASES.MINISTRIES_VOLUNTEERS}/${tab}`,
  getMinistryVolunteerRoute: (volunteerId: string, tab: VOLUNTEER_DETAILS_TAB = VOLUNTEER_DETAILS_TAB.GENERAL) =>
    `${ROUTE_ALIASES.MINISTRIES_VOLUNTEERS}/${volunteerId}/${tab}`,
  getCommunityRoute: (tab: COMMUNITY_TAB = COMMUNITY_TAB.BUSINESS) => `${ROUTE_ALIASES.COMMUNITY}/${tab}`,
  getCommunityListCardsRoute: (tab: COMMUNITY_TAB, listId: string) =>
    `${ROUTE_ALIASES.COMMUNITY}/${tab}/${ROUTE_ALIASES.COMMUNITY_LISTS}/${listId}`,
  getCommunityListSettingsRoute: (tab: COMMUNITY_TAB, listId: string) =>
    `${ROUTE_ALIASES.COMMUNITY}/${tab}/${ROUTE_ALIASES.COMMUNITY_LISTS}/${listId}/${ROUTE_ALIASES.COMMUNITY_LIST_SETTINGS}`,
  getMessageStatsRoute: (messagePage: MESSAGE_STATUS, id: string) => {
    const statusToRouteMap: Partial<Record<MESSAGE_STATUS, string>> = {
      [MESSAGE_STATUS.INBOX]: ROUTE_ALIASES.MESSAGES_INBOX,
      [MESSAGE_STATUS.SENT]: ROUTE_ALIASES.MESSAGES_SENT,
      [MESSAGE_STATUS.SCHEDULED]: ROUTE_ALIASES.MESSAGES_SCHEDULED,
      [MESSAGE_STATUS.DRAFT]: ROUTE_ALIASES.MESSAGES_DRAFTS,
      [MESSAGE_STATUS.DELETED]: ROUTE_ALIASES.MESSAGES_DELETED,
    };
    const baseRoute = statusToRouteMap[messagePage] ?? ROUTE_ALIASES.MESSAGES_INBOX;
    return `${baseRoute}/${id}/stats`;
  },
  getMessageCreateRoute: (recipients?: string[], recipientEmails?: string[], recipientDeliveryMethods?: string[][]) => {
    const params = new URLSearchParams();
    if (recipients && recipients.length > 0) {
      params.set('recipients', recipients.join(','));
    }
    const emails = recipientEmails?.map((email) => email.trim()) ?? [];
    if (emails.some(Boolean)) {
      params.set('recipientEmails', emails.join('|'));
    }
    const deliveryMethodGroups = recipientDeliveryMethods?.map((methods) => methods.join(',')) ?? [];
    if (deliveryMethodGroups.some(Boolean)) {
      params.set('recipientDeliveryMethods', deliveryMethodGroups.join('|'));
    }
    const queryString = params.toString();
    return queryString ? `${ROUTE_ALIASES.MESSAGES_CREATE}?${queryString}` : ROUTE_ALIASES.MESSAGES_CREATE;
  },
  getCalendarRoute: ({ isNewEventOpen = false, viewWeek = false }: GetCalendarRouteParams) => {
    const params = new URLSearchParams();
    if (isNewEventOpen) {
      params.set('isNewEventOpen', 'true');
    }
    if (viewWeek) {
      params.set('view', 'week');
    }
    const queryString = params.toString();
    return `${ROUTE_ALIASES.CALENDAR}${queryString ? `?${queryString}` : ''}`;
  },
};
